import type { UserProfile } from '../services/appsync-client'
import type { ChatUser, MessageChannelGroupId } from '../types/chat'
import { isBrunoUserId } from '@shared/bruno/constants'
import { isDeactivatedUser } from './deactivated-user'
import { isMember, isProvider, resolveProviderLevel } from './rbac'

/**
 * Slack/WhatsApp-style rules:
 * - DM: anyone can @ someone new → creates a group (like WhatsApp “add participant”).
 * - Group: members L1–L2 may @ only existing participants; L3+ may @ to add new members.
 */
export function canMentionToAddMember(
  profile: Pick<UserProfile, 'roleType' | 'providerLevel'>,
  channelGroupId: MessageChannelGroupId,
  participantIds: string[],
  targetUserId: string,
): boolean {
  if (isBrunoUserId(targetUserId)) return true
  if (channelGroupId === 'announcements') return true
  if (participantIds.includes(targetUserId)) return true
  if (channelGroupId === 'direct') {
    return resolveProviderLevel(profile) >= 3
  }
  return resolveProviderLevel(profile) >= 3
}

export function matchesMentionSearch(user: ChatUser, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  if (user.displayName.toLowerCase().includes(q)) return true
  const emailLocal = user.email.split('@')[0]?.toLowerCase() ?? ''
  return emailLocal.includes(q)
}

export function getAnnouncementMentionCandidates(
  search: string,
  directory: ChatUser[],
  currentUserId: string,
): ChatUser[] {
  return directory
    .filter((u) => u.userId !== currentUserId)
    .filter((u) => {
      if (isMember(u)) return true
      if (isProvider(u)) return resolveProviderLevel(u) >= 2
      return false
    })
    .filter((u) => matchesMentionSearch(u, search))
    .slice(0, 8)
}

export function getMentionCandidates(
  search: string,
  directory: ChatUser[],
  participantIds: string[],
  currentUserId: string,
  channelGroupId: MessageChannelGroupId,
  profile: Pick<UserProfile, 'roleType' | 'providerLevel'>,
): ChatUser[] {
  if (channelGroupId === 'announcements') {
    return getAnnouncementMentionCandidates(search, directory, currentUserId)
  }

  const canAdd = resolveProviderLevel(profile) >= 3
  const participantSet = new Set(participantIds)

  return directory
    .filter((u) => u.userId !== currentUserId)
    .filter((u) => {
      if (channelGroupId === 'direct') return true
      if (canAdd) return true
      return participantSet.has(u.userId)
    })
    .filter((u) => matchesMentionSearch(u, search))
    .sort((a, b) => {
      if (isBrunoUserId(a.userId)) return -1
      if (isBrunoUserId(b.userId)) return 1
      return 0
    })
    .slice(0, 8)
}

export function buildGroupTitleFromUsers(
  memberIds: string[],
  usersById: Map<string, ChatUser>,
  fallback = 'Group chat',
): string {
  const names = memberIds
    // Deactivated stubs must never appear in a name-built group title.
    .map((id) => {
      const user = usersById.get(id)
      return user && !isDeactivatedUser(user) ? user.displayName : undefined
    })
    .filter((n): n is string => Boolean(n?.trim()))
  if (names.length === 0) return fallback
  if (names.length <= 3) return names.join(', ')
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`
}
